import { useEffect, useState } from "react";
import { asText } from "../lib/utils";
import axios from "axios";
import { Loader2, X, Download, ExternalLink, Paperclip, Pencil, Lock, History, RefreshCw, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";
import useAuthStore from "@/store/authStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import PermButton from "@/components/PermButton";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtMoney = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "-");
const fmtTimestamp = (s) => (s ? new Date(s).toLocaleString() : "-");

export default function RMInwardBillDetailDialog({ open, onOpenChange, billId, onEdit, onDeleted }) {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [bill, setBill] = useState(null);
  const [error, setError] = useState("");
  // Edit history (Playfield + Zoho sources). Lazy-loaded on demand to avoid
  // a second round-trip on every detail open — most users won't expand it.
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Attachment preview — fetched as blob so the Authorization header is honoured
  const [previewUrl, setPreviewUrl] = useState("");
  // Retry-to-Zoho state for the attachment block
  const [zohoSyncing, setZohoSyncing] = useState(false);

  // Soft-delete UI state — modal + reason + in-flight guard. The
  // action is only surfaced when bill.status ∈ {PLAYFIELD_DRAFT,
  // REJECTED} (see the footer render below). We keep the confirm as a
  // nested Dialog so accidental clicks on "Delete Bill" don't
  // immediately hit the API.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleSoftDelete = async () => {
    if (!bill?.bill_id) return;
    setDeleting(true);
    try {
      const res = await axios.post(
        `${API}/rm-inward/bills/${encodeURIComponent(bill.bill_id)}/soft-delete`,
        { reason: deleteReason.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Bill ${bill.bill_id} deleted`);
      setConfirmDelete(false);
      setDeleteReason("");
      // Refresh in-dialog state so the DELETED banner renders
      // immediately, and hand off to the parent list so it can drop
      // the row (or refetch — parent's choice).
      setBill((prev) => (prev ? { ...prev, ...res.data } : prev));
      if (onDeleted) onDeleted(bill.bill_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete bill");
    } finally {
      setDeleting(false);
    }
  };

  const handleSyncAttachmentToZoho = async () => {
    if (!bill?.bill_id) return;
    setZohoSyncing(true);
    try {
      const res = await axios.post(
        `${API}/rm-inward/bills/${encodeURIComponent(bill.bill_id)}/attachment/sync-to-zoho`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Feb 2026 — the endpoint now returns `{synced, failed, skipped,
      // results}` after the shared-extraction refactor. Surface the full
      // summary (AC5) instead of just the legacy top-level document_id.
      const { synced = 0, failed = 0, skipped = 0, results = [] } = res.data || {};
      const firstErr = (results.find((r) => !r.synced && r.error) || {}).error;
      if (failed === 0 && synced > 0) {
        toast.success(`${synced} attachment${synced === 1 ? "" : "s"} synced to Zoho`);
      } else if (synced > 0 && failed > 0) {
        toast.warning(`${synced} synced, ${failed} failed — ${firstErr || "see details"}`);
      } else if (failed > 0) {
        toast.error(`${failed} attachment${failed === 1 ? "" : "s"} failed — ${firstErr || "see details"}`);
      } else {
        toast.info(`Nothing to sync (${skipped} skipped)`);
      }
      // Refetch the bill so the badge updates
      const fresh = await axios.get(`${API}/rm-inward/bills/${encodeURIComponent(bill.bill_id)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBill(fresh.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to sync attachment to Zoho");
    } finally {
      setZohoSyncing(false);
    }
  };

  useEffect(() => {
    if (!open || !billId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      setBill(null);
      try {
        const res = await axios.get(`${API}/rm-inward/bills/${encodeURIComponent(billId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setBill(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || "Failed to load bill");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, billId, token]);

  // When the bill changes, fetch the attachment as a blob URL for inline preview.
  useEffect(() => {
    let revoked = null;
    if (bill?.attachment && !bill.attachment.is_deleted && bill?.bill_id) {
      (async () => {
        try {
          const res = await axios.get(
            `${API}/rm-inward/bills/${encodeURIComponent(bill.bill_id)}/attachment`,
            { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
          );
          const url = URL.createObjectURL(res.data);
          revoked = url;
          setPreviewUrl(url);
        } catch {
          setPreviewUrl("");
        }
      })();
    } else {
      setPreviewUrl("");
    }
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [bill, token]);

  const handleDownloadAttachment = async () => {
    if (!bill?.bill_id) return;
    try {
      const res = await axios.get(
        `${API}/rm-inward/bills/${encodeURIComponent(bill.bill_id)}/attachment?download=true`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = bill.attachment?.original_filename || "vendor_invoice";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to download attachment");
    }
  };

  const handleOpenAttachmentInTab = () => {
    if (previewUrl) window.open(previewUrl, "_blank", "noopener,noreferrer");
  };

  // Lazy-fetch the edit history when the user toggles the History panel open.
  const loadHistory = async () => {
    if (history || historyLoading || !bill?.bill_id) return;
    setHistoryLoading(true);
    try {
      const res = await axios.get(
        `${API}/rm-inward/bills/${encodeURIComponent(bill.bill_id)}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistory(res.data?.history || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const isDraft = (bill?.status || "").toUpperCase() === "PLAYFIELD_DRAFT";

  const totals = bill?.totals || {};
  const items = bill?.line_items || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="bill-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 pr-8">
            <span>
              Bill Detail {bill?.bill_number ? `— ${bill.bill_number}` : ""}
              {bill?.zoho_synced && (
                <span className="ml-2 text-xs font-medium text-emerald-600">Synced to Zoho</span>
              )}
            </span>
            {bill && (
              <span className="flex items-center gap-2">
                {isDraft && onEdit ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(bill)}
                    data-testid="bill-edit-btn"
                  >
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                ) : null}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const next = !showHistory;
                    setShowHistory(next);
                    if (next) loadHistory();
                  }}
                  data-testid="bill-history-btn"
                >
                  <History className="w-3 h-3 mr-1" /> History
                </Button>
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {bill && !isDraft && bill.status !== "DELETED" && (
          <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900 flex items-start gap-2">
            <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Locked.</strong> Status is <span className="font-mono">{bill.status}</span>.
              Edits to finalised bills must be made inside Zoho Books — they&apos;ll sync back here automatically.
            </span>
          </div>
        )}

        {/* Feb 2026 — soft-deleted audit banner. Deleted bills are
            direct-fetchable by id for audit, but must clearly show
            they've been retired: who did it, when, from which prior
            status, and (if provided) why. Mirrors the FGInward
            CANCELLED banner convention (Trash2 icon, muted slate). */}
        {bill && bill.status === "DELETED" && (
          <div
            className="px-3 py-2 bg-zinc-100 border border-zinc-300 rounded-md text-xs text-zinc-700 flex items-start gap-2"
            data-testid="rm-inward-deleted-banner"
          >
            <Trash2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Deleted.</strong> Was <span className="font-mono">{bill.pre_delete_status || "—"}</span>,
              retired by <span className="font-medium">{bill.deleted_by_name || bill.deleted_by || "—"}</span>
              {bill.deleted_at && <> on {fmtTimestamp(bill.deleted_at)}</>}.
              {bill.delete_reason && <> Reason: <em>{bill.delete_reason}</em></>}
            </span>
          </div>
        )}

        {loading && (
          <div className="py-12 text-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading bill…
          </div>
        )}

        {error && (
          <div className="py-6 px-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        {bill && (
          <div className="space-y-4 text-sm">
            {/* Header grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 border rounded-md bg-zinc-50">
              <Field label="Vendor" value={bill.vendor_name} />
              <Field label="Branch" value={bill.branch} />
              <Field
                label="Receiving Location"
                value={
                  bill.receiving_vendor_id
                    ? (bill.receiving_vendor_name || bill.receiving_vendor_id)
                    : "Billed-to branch"
                }
                data-testid="bill-detail-receiving-location"
              />
              <Field label="Bill #" value={bill.bill_number} />
              <Field label="Bill Date" value={fmtDate(bill.bill_date)} />
              <Field label="Due Date" value={fmtDate(bill.due_date)} />
              <Field label="Order #" value={bill.order_number || "-"} />
              <Field label="Payment Terms" value={bill.payment_terms} />
              <Field label="Accounts Payable" value={bill.accounts_payable} />
              <Field label="Status" value={bill.status} />
              {bill.zoho_bill_number && <Field label="Zoho Bill #" value={bill.zoho_bill_number} />}
              {bill.tds_label && <Field label="TDS Applied" value={bill.tds_label} />}
              {bill.reverse_charge && <Field label="Reverse Charge" value="Yes" />}
            </div>

            {bill.notes && (
              <div className="p-3 border rounded-md">
                <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1">Notes</div>
                <div className="text-sm whitespace-pre-wrap">{bill.notes}</div>
              </div>
            )}

            {/* Vendor invoice attachment (if present) */}
            {bill.attachment && !bill.attachment.is_deleted && (
              <div className="p-3 border rounded-md" data-testid="bill-attachment-block">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-2">
                    <Paperclip className="w-3 h-3" /> Vendor Invoice
                    {/* Zoho sync badge — green tick if synced, amber warning if push failed */}
                    {bill.zoho_bill_id && (
                      bill.attachment.zoho_synced ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="bill-attachment-zoho-badge-synced" title={`Zoho doc: ${bill.attachment.zoho_document_id || "?"}`}>
                          <CheckCircle2 className="w-2.5 h-2.5" /> Synced to Zoho
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300" data-testid="bill-attachment-zoho-badge-pending" title={bill.attachment.zoho_last_error || "Not yet synced to Zoho"}>
                          <AlertTriangle className="w-2.5 h-2.5" /> Not synced to Zoho
                        </span>
                      )
                    )}
                  </div>
                  <div className="flex gap-2">
                    {bill.zoho_bill_id && !bill.attachment.zoho_synced && (
                      <Button
                        variant="outline" size="sm"
                        onClick={handleSyncAttachmentToZoho}
                        disabled={zohoSyncing}
                        data-testid="bill-attachment-sync-to-zoho-btn"
                      >
                        {zohoSyncing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                        Sync to Zoho
                      </Button>
                    )}
                    <Button
                      variant="outline" size="sm"
                      onClick={handleOpenAttachmentInTab}
                      disabled={!previewUrl}
                      data-testid="bill-attachment-view-btn"
                    >
                      <ExternalLink className="w-3 h-3 mr-1" /> Open
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={handleDownloadAttachment}
                      data-testid="bill-attachment-download-btn"
                    >
                      <Download className="w-3 h-3 mr-1" /> Download
                    </Button>
                  </div>
                </div>
                {previewUrl ? (
                  (bill.attachment.content_type || "").toLowerCase() === "application/pdf" ? (
                    // PDF — render via <object> so the browser's native PDF
                    // viewer takes over. <img> can't display PDFs, and
                    // <iframe> sometimes breaks on Safari. Fallback link is
                    // always shown for download.
                    <object
                      data={previewUrl}
                      type="application/pdf"
                      className="w-full h-[400px] border rounded"
                      aria-label={bill.attachment.original_filename || "Vendor invoice PDF"}
                      data-testid="bill-attachment-pdf"
                    >
                      <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline text-sm">
                        {bill.attachment.original_filename || "Open PDF"}
                      </a>
                    </object>
                  ) : (
                    <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                      <img
                        src={previewUrl}
                        alt={bill.attachment.original_filename || "Vendor invoice"}
                        className="max-h-[400px] w-auto border rounded"
                        data-testid="bill-attachment-img"
                      />
                    </a>
                  )
                ) : (
                  <div className="text-xs text-zinc-500 py-3">Loading attachment…</div>
                )}
                <div className="text-[10px] text-zinc-500 mt-2 font-mono">
                  {bill.attachment.original_filename || "-"} • {(Number(bill.attachment.size || 0) / 1024).toFixed(0)} KB • uploaded {fmtDate(bill.attachment.uploaded_at)}
                </div>

                {/* All attachments — Feb 2026 multi-file support. Shows every
                    non-deleted entry in `attachments[]` as a clickable row
                    that streams the specific file from object storage. */}
                {Array.isArray(bill.attachments) && bill.attachments.filter(a => !a?.is_deleted).length > 1 && (
                  <div className="mt-3 pt-3 border-t" data-testid="bill-attachments-list">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
                      All vendor invoice files ({bill.attachments.filter(a => !a?.is_deleted).length})
                    </div>
                    <div className="space-y-1">
                      {bill.attachments.filter(a => !a?.is_deleted).map((a) => (
                        <div
                          key={a.id || a.storage_path}
                          className="flex items-center justify-between gap-2 bg-zinc-50 border rounded px-2 py-1 text-[11px]"
                          data-testid={`bill-attachment-row-${a.id || ""}`}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Paperclip className="w-3 h-3 text-zinc-400 shrink-0" />
                            <span className="truncate text-zinc-800 font-medium" title={a.original_filename}>
                              {a.original_filename || "(unnamed)"}
                            </span>
                            <span className="text-zinc-500 whitespace-nowrap font-mono text-[10px]">
                              {(Number(a.size || 0) / 1024).toFixed(0)} KB
                            </span>
                            {a.zoho_synced ? (
                              <span className="text-[9px] font-mono uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-100 text-emerald-800">
                                Zoho ✓
                              </span>
                            ) : null}
                          </div>
                          <a
                            href={`${API}/rm-inward/bills/${encodeURIComponent(bill.bill_id)}/attachments/${encodeURIComponent(a.id)}?download=true&auth=${encodeURIComponent(token)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-[11px] font-medium"
                            data-testid={`bill-attachment-download-${a.id}`}
                          >
                            Download
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Line items */}
            <div className="border rounded-md overflow-hidden">
              <div className="px-3 py-2 bg-zinc-50 border-b text-xs font-medium uppercase tracking-wide">
                Item Details ({items.length})
              </div>
              <table className="w-full text-xs">
                <thead className="bg-white border-b">
                  <tr>
                    <Th>RM ID</Th>
                    <Th>Description</Th>
                    <Th>HSN</Th>
                    <Th className="text-right">Qty</Th>
                    <Th>Unit</Th>
                    <Th className="text-right">Rate</Th>
                    <Th className="text-right">Tax</Th>
                    <Th className="text-right">Amount</Th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map((li, i) => (
                    <tr key={i} data-testid={`bill-line-${i}`}>
                      <Td className="font-mono">{li.rm_id}</Td>
                      <Td>{asText(li.description) || li.rm_name || "-"}</Td>
                      <Td className="font-mono">{asText(li.hsn) || "-"}</Td>
                      <Td className="text-right font-mono">{li.quantity}</Td>
                      <Td className="font-mono">{li.unit || "-"}</Td>
                      <Td className="text-right font-mono">{fmtMoney(li.rate)}</Td>
                      <Td className="text-right font-mono">{fmtMoney(li.tax_amount)}</Td>
                      <Td className="text-right font-mono font-medium">{fmtMoney(li.amount)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="ml-auto max-w-sm border rounded-md p-3 space-y-1.5">
              <Row label="Sub Total" value={fmtMoney(totals.sub_total)} />
              {totals.discount_amount > 0 && (
                <Row label={`Discount${totals.discount_type === "percentage" ? ` (${totals.discount_value}%)` : ""}`}
                     value={`- ${fmtMoney(totals.discount_amount)}`} negative />
              )}
              {totals.tds_tcs && totals.tds_tcs !== "NONE" && (
                <Row label={`TDS${bill.tds_label ? ` (${bill.tds_label})` : ""}`}
                     value={`- ${fmtMoney(totals.tds_tcs_amount)}`} negative />
              )}
              <Row label="Tax Total" value={`+ ${fmtMoney(totals.tax_total)}`} />
              <Separator />
              <Row label="Grand Total" value={fmtMoney(totals.grand_total)} bold />
            </div>
          </div>
        )}

        {/* Edit history panel — lazy-loaded on first toggle */}
        {bill && showHistory && (
          <div className="border rounded-md p-3 space-y-2" data-testid="bill-history-panel">
            <div className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1">
              <History className="w-3 h-3" /> Edit History
            </div>
            {historyLoading && (
              <div className="text-xs text-zinc-500 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading…
              </div>
            )}
            {!historyLoading && history && history.length === 0 && (
              <div className="text-xs text-zinc-500">No edits recorded yet.</div>
            )}
            {!historyLoading && history && history.length > 0 && (
              <ul className="space-y-2 text-xs">
                {history.map((h) => (
                  <li key={h.id} className="border-l-2 pl-3 py-1 border-zinc-300">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded ${
                          h.source === "zoho"
                            ? "bg-blue-50 text-blue-700"
                            : "bg-emerald-50 text-emerald-700"
                        }`}
                      >
                        {h.source}
                      </span>
                      <span className="font-medium">{h.actor_name || "-"}</span>
                      <span className="text-zinc-500">• {fmtTimestamp(h.ts)}</span>
                    </div>
                    {h.diff && Object.keys(h.diff).length > 0 && (
                      <div className="mt-1 font-mono text-[11px] text-zinc-600">
                        {Object.keys(h.diff).join(", ")}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex justify-between items-center pt-2 gap-2">
          {/* Feb 2026 — surface the delete action ONLY on the two
              allowed statuses (PLAYFIELD_DRAFT / REJECTED / PUSH_FAILED). Any other
              status hides the button so the user can't even attempt
              a call the backend would 400 on. RBAC still enforced via
              PermButton (entity=RMInwardBill, action=DELETE). */}
          <div>
            {/* Oct 2026 — PUSH_FAILED joins the deletable set (FG Inward parity) */}
            {bill && ["PLAYFIELD_DRAFT", "REJECTED", "PUSH_FAILED"].includes(bill.status) && (
              <PermButton
                entity="RMInwardBill"
                action="DELETE"
                variant="destructive"
                onClick={() => setConfirmDelete(true)}
                className="gap-1"
                data-testid="rm-inward-soft-delete-btn"
              >
                <Trash2 className="w-4 h-4" /> Delete Bill
              </PermButton>
            )}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bill-detail-close">
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
        </div>

        {/* Soft-delete confirm — nested inside the detail dialog so
            it inherits the modal stack (Radix handles the focus trap
            correctly here). Renders only for the two allowed statuses
            (same guard as the trigger button); the backend enforces
            the same rule again as a defense-in-depth check. */}
        <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <DialogContent className="max-w-md" data-testid="rm-inward-soft-delete-dialog">
            <DialogHeader><DialogTitle>Delete this bill?</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <p>
                {bill?.status === "PLAYFIELD_DRAFT" ? (
                  <>This bill was never pushed to Zoho and never touched inventory — deleting it just retires the record for audit.</>
                ) : (
                  <>This bill was rejected by Zoho. Any stock reversal has already been handled by the reject flow — deleting only retires the record for audit.</>
                )}
                {" "}The document stays in the database and can still be looked up by bill number.
              </p>
              <div>
                <Label>Reason (optional)</Label>
                <Textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  rows={2}
                  placeholder="Why is this being deleted?"
                  data-testid="rm-inward-soft-delete-reason"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                  data-testid="rm-inward-soft-delete-cancel"
                >
                  Keep Bill
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleSoftDelete}
                  disabled={deleting}
                  data-testid="rm-inward-soft-delete-confirm"
                >
                  {deleting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Deleting…</> : <>Delete Bill</>}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
    <div className="text-sm font-medium">{value || "-"}</div>
  </div>
);

const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2 text-left font-medium text-zinc-600 ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;

const Row = ({ label, value, bold, negative }) => (
  <div className={`flex justify-between text-sm ${bold ? "font-bold text-base" : ""}`}>
    <span>{label}</span>
    <span className={`font-mono ${negative ? "text-red-600" : ""}`}>{value}</span>
  </div>
);
